import { PhoneNumber } from "../api/models/mainservice";
import { useCustomTranslation } from "./translateHelpers";
import { getI18n } from "react-i18next";
import { parsePhoneNumber } from "libphonenumber-js/max";

// Only one pattern rule can be used at one time, if we want more complex validation we must to use validate function

export const useValidators = () => {
  const { tError } = useCustomTranslation();

  const required = {
    value: true,
    message: tError("required"),
  };

  const minLength = (min: number) => ({
    value: min,
    message: tError("min_length", { min }),
  });

  const maxLength = (max: number) => ({
    value: max,
    message: tError("max_length", { max }),
  });

  const max = (max: number) => ({
    value: max,
    message: tError("max_value", { max }),
  });

  const min = (min: number) => ({
    value: min,
    message: tError("min_value", { min }),
  });

  const lowercaseChar = {
    pattern: {
      value: /[a-z]/g,
      message: tError("missing_lowercase_char"),
    },
  };

  const uppercaseChar = {
    pattern: {
      value: /[A-Z]/g,
      message: tError("missing_uppercase_char"),
    },
  };

  const specialChar = {
    pattern: {
      value: /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g,
      message: tError("missing_special_char"),
    },
  };

  const numberChar = {
    pattern: {
      value: /[0-9]/g,
      message: tError("missing_number_char"),
    },
  };

  const validatePhoneNumber = (value?: PhoneNumber, required?: boolean) => {
    let isValid = false;

    if (required && !value?.number) {
      return tError("required");
    }

    if (value?.number) {
      try {
        const phoneNumber = parsePhoneNumber(`${value.prefix}${value.number}`);
        isValid = phoneNumber.isValid();
      } catch {
        // parsePhoneNumber throws if input is not a valid phone number format
        // Return false for validation - error message will be shown
        isValid = false;
      }
    }

    return value?.number && !isValid
      ? tError("invalid_phone_number")
      : undefined;
  };

  return {
    required,
    lowercaseChar,
    uppercaseChar,
    specialChar,
    numberChar,
    validatePhoneNumber,
    minLength,
    maxLength,
    max,
    min,
  };
};

export const passwordComplexityValidation = (value: string) => {
  const { t } = getI18n();

  if (!/[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g.test(value)) {
    return t("missing_special_char", { ns: "errors" });
  }

  if (!/[0-9]/g.test(value)) {
    return t("missing_number_char", { ns: "errors" });
  }

  if (!/[A-Z]/g.test(value)) {
    return t("missing_uppercase_char", { ns: "errors" });
  }

  if (!/[a-z]/g.test(value)) {
    return t("missing_lowercase_char", { ns: "errors" });
  }
};

export const samePasswordCheck = (value: string, originalPassword?: string) => {
  const { t } = getI18n();

  if (value !== originalPassword) {
    return t("passwords_not_match", { ns: "errors" });
  }
};
